import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Phone, MessageSquare, CheckCircle, Volume2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { businessInfo } from '../data/mock';

const RollersPage = () => {
  const rollerTypes = [
    {
      title: "Steel Rollers",
      description: "Durable and cost-effective for standard use in Saskatoon garages.",
      features: ["Budget-friendly", "Durable construction", "Standard performance"]
    },
    {
      title: "Nylon Rollers",
      description: "Quiet operation perfect for attached garages in Saskatoon homes.",
      features: ["Ultra-quiet", "Smooth rolling", "Less maintenance", "Ideal for attached garages"]
    }
  ];

  const faqItems = [
    {
      q: "How often should garage door rollers be replaced?",
      a: "Rollers typically last 5-7 years with regular use. Saskatoon homeowners who use their garage frequently may need replacement sooner."
    },
    {
      q: "What causes garage door noise?",
      a: "Worn rollers are a common cause of garage door noise in Saskatoon. Replacing old steel rollers with nylon rollers dramatically reduces noise."
    },
    {
      q: "Can I replace rollers myself?",
      a: "While some rollers can be replaced DIY, rollers near springs require professional service for safety. We recommend professional replacement for Saskatoon homeowners."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <section className="pt-32 pb-12 bg-gradient-to-r from-blue-900 to-blue-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Garage Door Rollers Saskatoon</h1>
            <p className="text-xl text-blue-100 mb-8">Professional roller replacement for quiet operation in Saskatoon</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button onClick={() => window.location.href = `tel:${businessInfo.phone}`} size="lg" className="bg-green-600 hover:bg-green-700 text-white font-bold px-8 py-6 text-lg">
                <Phone size={20} className="mr-2" />Call {businessInfo.phone}
              </Button>
              <Button onClick={() => window.location.href = '/quote'} size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 font-bold px-8 py-6 text-lg">
                <MessageSquare size={20} className="mr-2" />Get Free Quote
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 mb-4">
              Garage door rollers allow your door to move smoothly along the tracks in Saskatoon homes. When rollers wear out, you'll hear grinding noises and feel rough operation. <strong>306 Garage Doors</strong> replaces worn rollers with high-quality options for quiet, smooth performance serving Saskatoon homeowners.
            </p>
            <p className="text-lg text-gray-700">
              Upgrading to nylon rollers can dramatically reduce noise in attached garages, making daily life quieter for Saskatoon families.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Roller Types for Saskatoon Homes</h2>
              <p className="text-xl text-gray-600">Choose the right rollers for your needs</p>
            </div>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {rollerTypes.map((type, idx) => (
                <Card key={idx} className="border-2 hover:border-blue-600 hover:shadow-xl transition-all">
                  <CardHeader>
                    <CardTitle className="text-2xl text-blue-800">{type.title}</CardTitle>
                    <p className="text-gray-600 mt-2">{type.description}</p>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {type.features.map((feature, fidx) => (
                        <li key={fidx} className="flex items-start gap-2">
                          <CheckCircle size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Volume2 size={64} className="text-blue-700 mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Reduce Garage Door Noise</h2>
            <p className="text-xl text-gray-700">
              Worn rollers are the #1 cause of noisy garage doors in Saskatoon. Upgrading to nylon rollers provides whisper-quiet operation, perfect for attached garages or homes with living spaces near the garage.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqItems.map((item, idx) => (
                <Card key={idx} className="border-2">
                  <CardHeader>
                    <CardTitle className="text-lg text-gray-900">{item.q}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700">{item.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-green-600 to-green-700">
        <div className="container mx-auto px-4 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Need Roller Replacement in Saskatoon?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">Professional roller replacement for quiet, smooth garage door operation in Saskatoon.</p>
          <Button onClick={() => window.location.href = `tel:${businessInfo.phone}`} size="lg" className="bg-white text-green-700 hover:bg-gray-100 font-bold px-12 py-8 text-xl">
            <Phone size={24} className="mr-2" />Call {businessInfo.phone}
          </Button>
          <p className="mt-6 text-green-100 italic">{businessInfo.confidentialMessage}</p>
        </div>
      </section>
    </div>
  );
};

export default RollersPage;